import { FormEvent, Suspense, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import classNames from "classnames";
import {
  emitEventOnPromptButtonClickAtom,
  templateExecutionFailedErrorMsgAtom,
  isAiPageAtom,
} from "@zapier/canvas-state";
import {
  Alert,
  AlertDescription,
  Icon,
  Spinner,
  Tag,
  Text,
  TextInput,
} from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";

import { outlineAtom } from "../../../atoms/outlineAtom";
import { aiPromptAtomResettable } from "../../../atoms/aiPromptAtom";
import { goBackToWelcomeModalCommand } from "../../../atoms/goBackToWelcomeModalCommand";
import { useAutoSelectAiPrompt } from "../../../hooks/useAutoSelectAiPrompt";
import { useRevealQueue } from "../../../hooks/useRevealTimeout";
import { fixAndParseJSON, systems } from "../../../utils";
import { onboardingDataAsyncAtom } from "../../../atoms/onboardingDataAsyncAtom";
import { TypingComponent } from "../../TypingComponent";
import { Confetti } from "../../Confetti";
import { OptionPill } from "../OptionPill";
import { Placeholder } from "../Placeholder";
import { InterfacesLoader } from "../InterfacesLoader";
import { TablesLoader } from "../TablesLoader";
import { ErrorBoundary } from "../../ErrorBoundary";
import { ZapLoader } from "../ZapLoader";
import styles from "./AIStreamModalContent.module.css";

type Props = {
  onFormSubmit: (localPrompt: string) => void;
  handleCloseModal: () => void;
  isLoading: boolean;
  isComplete: boolean | null;
  error: string;
};

// ## IF STREAMING DOESN"T WORK LOOK AT THE PROXY
// The streaming endpoint has to be proxied directly for some reason
// In production it will be routed through cloudfront to the ZSL Creator API
// If streaming doesn't work locally, there's always money in the banana stand - I mean look at the proxy
// https://gitlab.com/zapier/zapier-terraform/-/blob/master/production/cloudfront.tf

export function AIStreamModalContent({
  onFormSubmit,
  handleCloseModal,
  isLoading,
  isComplete,
  error,
}: Props) {
  const inputRef = useAutoSelectAiPrompt();

  const templateExecutionFailed = useAtomValue(
    templateExecutionFailedErrorMsgAtom,
  );

  const submittedPrompt = useAtomValue(aiPromptAtomResettable);
  const [localPrompt, setLocalPrompt] = useState(submittedPrompt);
  const stringOutline = useAtomValue(outlineAtom);
  const outline = fixAndParseJSON(stringOutline);
  const name = outline?.name;
  const description = outline?.description;
  const isAiPage = useAtomValue(isAiPageAtom);

  const [activeOptionPill, setActiveOptionPill] = useState<string | null>(null);
  const goBackToWelcomeModal = useSetAtom(goBackToWelcomeModalCommand);

  function getGeneratingText() {
    if (templateExecutionFailed) {
      return "Regenerate system";
    }

    if (isComplete === null) {
      return "Generate system";
    }

    const isLoadingInterfaces =
      isLoading &&
      (outline?.interfacePages || outline?.interfacePrompts) &&
      !outline?.tables &&
      !outline?.tablesPrompts;
    const isLoadingTables =
      isLoading &&
      (outline?.tables || outline?.tablesPrompts) &&
      !outline?.zapPrompts;
    const isLoadingZaps = isLoading && outline?.zapPrompts && !isComplete;

    if (isLoadingInterfaces) {
      return "Generating Interfaces";
    }
    if (isLoadingTables) {
      return "Generating Tables";
    }
    if (isLoadingZaps) {
      return "Generating Zaps";
    }

    return "Generating system";
  }

  function handleSetPrompt(text: string) {
    if (text === "") {
      setActiveOptionPill(null);
    }
    setLocalPrompt(text);
  }

  const { revealedKeys } = useRevealQueue(
    outline,
    ["name", "description", "interfaces", "tables", "zaps"],
    4000,
    Boolean(isComplete),
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    onFormSubmit(localPrompt);
  };

  return (
    <div className={styles.contentWrapper}>
      <form onSubmit={handleSubmit} className={styles.createASystem}>
        <div className={styles.header}>
          <span className={styles.headerTextWrapper}>
            <Icon name="miscAI" size={24} color="UiPrimaryStrongest" />
            <Text type="SectionHeader">Create a system</Text>
            <Tag size="small" variant="simple">
              Beta
            </Tag>
          </span>
          <Text type="Body1">
            Let Copilot build your system by choosing a pre-built prompt or
            dream up your own system and watch as we generate the rest.
          </Text>
        </div>
        {templateExecutionFailed && (
          <Alert status="error" title="Error generating assets" variant="alert">
            <AlertDescription>
              {templateExecutionFailed || "Update your prompt and try again."}
            </AlertDescription>
          </Alert>
        )}
        <Pills
          activeOptionPill={activeOptionPill}
          setActiveOptionPill={setActiveOptionPill}
          setLocalPrompt={setLocalPrompt}
        />

        <div className={styles.input}>
          <TextInput
            type="submit"
            value={localPrompt}
            onChange={(e) => handleSetPrompt(e.target.value)}
            isFullWidth={true}
            isMultiline={true}
            rows={6}
            placeholder="Describe the problem you need help solving. The more detailed the problem, the better the solution!"
            ref={inputRef}
          />
          {error && (
            <Text type="SmallPrint1" color="StatusErrorStronger">
              {error}
            </Text>
          )}
        </div>
        <span className={styles.buttonWrapper}>
          {!isLoading && !isAiPage && (
            <Button
              variant="tertiary"
              size="medium"
              onPress={goBackToWelcomeModal}
            >
              <div className={styles.iconButton}>
                {isComplete === null && <Icon name="arrowBigLeft" size={26} />}
                <span>Back</span>
              </div>
            </Button>
          )}
          {!isComplete && (
            <Button
              variant="primary"
              type="submit"
              isDisabled={!localPrompt.trim() || isComplete === false}
              size="medium"
            >
              <div className={styles.iconButton}>
                {isComplete === null && <Icon name="miscAI" size={26} />}
                {isComplete === false && <Spinner size="small" color="dark" />}
                <span>{getGeneratingText()}</span>
              </div>
            </Button>
          )}
          {isComplete === true && (
            <Button
              variant="primary"
              type="submit"
              size="medium"
              onPress={handleCloseModal}
            >
              <div className={styles.iconButton}>
                <Icon name="navCanvas" size={26} />
                <span>See workflow in Canvas</span>
              </div>
            </Button>
          )}
        </span>
      </form>
      <div className={styles.loadingState}>
        <div
          className={classNames(styles.loadingHeader, {
            [styles.loadingHeaderIsLoading]:
              isLoading && !outline.interfacePages,
          })}
        >
          <div className={styles.sectionHeader}>
            {name && isLoading && revealedKeys.includes("name") && (
              <TypingComponent>{name}</TypingComponent>
            )}
            {name && !isLoading && name}
            {!name && "Your system awaits..."}
          </div>
          <span className={styles.loadingDescription}>
            {description && (
              <Text type="Body1">
                {isLoading && revealedKeys.includes("description") && (
                  <TypingComponent>{description}</TypingComponent>
                )}
                {!isLoading && description}
              </Text>
            )}
            {!description && (
              <>
                <Placeholder width={432} />
                <Placeholder width={400} />
              </>
            )}
          </span>
        </div>

        <InterfacesLoader
          zsl={outline}
          isLoading={isLoading}
          isShown={
            revealedKeys.includes("interfaces") ||
            isComplete === null ||
            isComplete === true
          }
        />
        <TablesLoader
          zsl={outline}
          isLoading={isLoading}
          isShown={
            revealedKeys.includes("tables") ||
            isComplete === null ||
            isComplete === true
          }
        />
        <ZapLoader
          zsl={outline}
          isLoading={isLoading}
          isShown={
            revealedKeys.includes("zaps") ||
            isComplete === null ||
            isComplete === true
          }
        />
      </div>
      {isComplete && <Confetti />}
    </div>
  );
}

type PillsProps = {
  activeOptionPill: string | null;
  setActiveOptionPill: (value: string | null) => void;
  setLocalPrompt: (text: string) => void;
};

function SuspendedPills({
  activeOptionPill,
  setActiveOptionPill,
  setLocalPrompt,
}: PillsProps) {
  const onboardingData = useAtomValue(onboardingDataAsyncAtom);

  const emitEventOnPromptButtonClick = useSetAtom(
    emitEventOnPromptButtonClickAtom,
  );

  const role = onboardingData.identity.role ?? "Popular";

  const relevantSystems = systems.find((system) => system.role === role);

  function setOption(optionSlug: string) {
    const text = relevantSystems?.systems?.find(
      (system) => system.key === optionSlug,
    )?.prompt;

    setActiveOptionPill(optionSlug);
    setLocalPrompt(text || "");

    emitEventOnPromptButtonClick({ rawText: optionSlug, optionSelected: role });
  }

  return (
    <div className={styles.prefills}>
      {relevantSystems?.systems?.map((option) => (
        <OptionPill
          key={option.key}
          onClick={() => setOption(option.key)}
          isActive={activeOptionPill === option.key}
        >
          {option.systemTitle}
        </OptionPill>
      ))}
    </div>
  );
}

function Pills({
  activeOptionPill,
  setActiveOptionPill,
  setLocalPrompt,
}: PillsProps) {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <SuspendedPills
          activeOptionPill={activeOptionPill}
          setActiveOptionPill={setActiveOptionPill}
          setLocalPrompt={setLocalPrompt}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
